import React from "react";
import { ArrowRightOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { Localize } from "components/service";
import UserCompanyField from "components/users/fields/user-company-field";
import UserInfoField from "components/users/fields/user-info-field";
import UserRoleField from "components/users/fields/user-role-field";
import StaffManagementConst from "../staff-management-const";
import WorkerTooltip from "../fields/staff-worker-tooltip";
import {
    STAFF_WORKER_STATUS,
    STAFF_WORKER_STATUS_TRANSLATIONS,
    formatStaffWorkerDuration,
    getStaffWorkerRoleColor,
} from "./staff-workers-api-helper";


const EMPTY_VALUE = "—";
const OPEN_WORKER_STATUSES = [
    STAFF_WORKER_STATUS.checkedIn,
    STAFF_WORKER_STATUS.missedCheckout,
    STAFF_WORKER_STATUS.failedCheckout,
];
const VIEW_SESSION_LABEL = "STAFF_MANAGEMENT.WorkersAction_ViewSession";

const getHoursHint = (summary, totalHours) => {
    if (totalHours)
    {
        return summary?.isTotalPartial ? "STAFF_MANAGEMENT.WorkersHours_PartialHint" : undefined;
    }

    const hasOpenSession = OPEN_WORKER_STATUSES.includes(summary?.status);

    if (summary?.hasIncompleteHours)
    {
        return hasOpenSession ?
            "STAFF_MANAGEMENT.WorkersHours_UnavailableOpenHint" :
            "STAFF_MANAGEMENT.WorkersHours_UnavailableHint";
    }

    return hasOpenSession ? "STAFF_MANAGEMENT.WorkersHours_NoCompletedHint" : undefined;
};

const TextCell = ({ children, extraClass = "" }) => (
    <span
        className={ `staff-workers-table__cell ${ extraClass }` }
        title={ typeof children === "string" && children ? children : undefined }>
        { children || EMPTY_VALUE }
    </span>
);

const WorkerStatusCell = ({ status, viewSessionID, eeID }) => {
    const translation = STAFF_WORKER_STATUS_TRANSLATIONS[ status ];
    const statusText = (
        <TextCell extraClass={ `staff-workers-table__status staff-workers-table__status--${ status || "unavailable" }` }>
            { translation ? <Localize>{ translation }</Localize> : EMPTY_VALUE }
        </TextCell>
    );
    const canView = eeID && viewSessionID && (
        status === STAFF_WORKER_STATUS.checkedOut || OPEN_WORKER_STATUSES.includes(status)
    );

    return canView ? (
        <span className="staff-workers-table__status-with-view">
            { statusText }
            <WorkerTooltip translationKey={ VIEW_SESSION_LABEL }>
                <Link
                    className="staff-workers-table__icon staff-workers-table__view"
                    to={ {
                        pathname: `${ StaffManagementConst.basePath }/${ eeID }/attendance`,
                        state: { viewWorkSessionID: String(viewSessionID) },
                    } }>
                    <ArrowRightOutlined aria-hidden="true" />
                    <span className="visually-hidden"><Localize>{ VIEW_SESSION_LABEL }</Localize></span>
                </Link>
            </WorkerTooltip>
        </span>
    ) : statusText;
};

const StaffWorkersTableHelper = {
    columns: [
        {
            title: <Localize>TABLES.Column_Title_Name</Localize>,
            dataIndex: "name",
            width: 188,
        },
        {
            title: <Localize>TABLES.Column_Title_Phone</Localize>,
            dataIndex: "phone",
            width: 176,
        },
        {
            title: <Localize>TABLES.Column_Title_Email</Localize>,
            dataIndex: "email",
            width: 258,
        },
        {
            title: <Localize>STAFF_MANAGEMENT.Column_Subcontractor</Localize>,
            dataIndex: "subcontractor",
            width: 177,
        },
        {
            title: <Localize>TABLES.Column_Title_UserGroup</Localize>,
            dataIndex: "role",
            width: 238,
        },
        {
            title: <Localize>STAFF_MANAGEMENT.Column_Status</Localize>,
            dataIndex: "status",
            width: 190,
        },
        {
            title: <Localize>STAFF_MANAGEMENT.Column_TotalHours</Localize>,
            dataIndex: "totalHours",
            width: 141,
        },
    ],

    data: (users = [], currentObj = {}) => (users || []).filter(Boolean).map((user) => {
        const role = user.role || {};
        const sessionSummary = currentObj.sessionSummaries?.[ String(user.id) ];
        const totalHours = formatStaffWorkerDuration(sessionSummary?.totalWorkedSeconds);
        const hoursHint = getHoursHint(sessionSummary, totalHours);

        return {
            key: user.id,
            name: (
                <TextCell>
                    <UserInfoField
                        className="staff-workers-table__name"
                        emptyValue={ EMPTY_VALUE }
                        linked={ false }
                        user={ user } />
                </TextCell>
            ),
            phone: <TextCell>{ user.phone }</TextCell>,
            email: <TextCell extraClass="staff-workers-table__email">{ user.email }</TextCell>,
            subcontractor: (
                <TextCell>
                    <UserCompanyField
                        className="staff-workers-table__subcontractor"
                        emptyValue={ EMPTY_VALUE }
                        linked={ false }
                        subcontractor={ user.subcontractor } />
                </TextCell>
            ),
            role: (
                <TextCell>
                    <UserRoleField
                        className="staff-workers-table__role"
                        emptyValue={ EMPTY_VALUE }
                        linked={ false }
                        role={ {
                            ...role,
                            colorHex: getStaffWorkerRoleColor(role.colorHex),
                        } } />
                </TextCell>
            ),
            status: <WorkerStatusCell
                status={ sessionSummary?.status }
                viewSessionID={ sessionSummary?.viewSessionID }
                eeID={ currentObj.eeID } />,
            totalHours: (
                <TextCell extraClass={ `staff-workers-table__total-hours${ totalHours ? "" : " staff-workers-table__unavailable" }` }>
                    { hoursHint ? (
                        <>
                            { totalHours || EMPTY_VALUE }
                            <WorkerTooltip translationKey={ hoursHint } trigger={ [ "hover", "focus", "click" ] }>
                                <button type="button" className="staff-workers-table__icon">
                                    <InfoCircleOutlined aria-hidden="true" />
                                    <span className="visually-hidden"><Localize>{ hoursHint }</Localize></span>
                                </button>
                            </WorkerTooltip>
                        </>
                    ) : totalHours || EMPTY_VALUE }
                </TextCell>
            ),
        };
    }),
};

export default StaffWorkersTableHelper;
